import { useMemo } from 'react';
import { Client } from 'boardgame.io/react';
import { I18nProvider } from './i18n';
import { getGameEntry, GAME_REGISTRY } from './config/gameRegistry';
import { MapEditor } from './components/editor';
import { MapSelectionScreen } from './components/MapSelectionScreen';
import { HallOfHonor } from './components/HallOfHonor';
import { AchievementsScreen } from './components/AchievementsScreen';
import { StatisticsScreen } from './components/StatisticsScreen';
import { ReplayViewer } from './components/ReplayViewer';
import { BASE_PATH } from './config';

type AppMode = 'map-selection' | 'game' | 'editor' | 'hall-of-honor' | 'achievements' | 'statistics' | 'replay';

interface AppRouteInfo {
  mode: AppMode;
  mapId: string | null;
}

/**
 * URLパスからモードとマップIDを判定
 * - /aos_ai/editor/ → editor
 * - /aos_ai/barbados/ → game (barbados)
 * - /aos_ai/puerto-rico/ → game (puerto-rico)
 * - /aos_ai/ → map-selection
 */
function getAppRouteInfo(): AppRouteInfo {
  const path = window.location.pathname;

  // エディタモード
  if (path.includes('/editor')) {
    return { mode: 'editor', mapId: null };
  }

  // Hall of Honor
  if (path.includes('/hall-of-honor')) {
    return { mode: 'hall-of-honor', mapId: null };
  }

  // Achievements
  if (path.includes('/achievements')) {
    return { mode: 'achievements', mapId: null };
  }

  // Statistics
  if (path.includes('/statistics')) {
    return { mode: 'statistics', mapId: null };
  }

  // Replay Viewer
  if (path.includes('/replay')) {
    return { mode: 'replay', mapId: null };
  }

  // マップIDを抽出: {BASE_PATH}{mapId}/ の形式
  // BASE_PATH の末尾スラッシュを除去してエスケープ
  const escapedBasePath = BASE_PATH.replace(/\/$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const mapIdPattern = new RegExp(`${escapedBasePath}/([a-z0-9_-]+)/?$`, 'i');
  const match = path.match(mapIdPattern);
  if (match) {
    const mapId = match[1];
    // 登録されているマップか確認
    if (GAME_REGISTRY[mapId]) {
      return { mode: 'game', mapId };
    }
  }

  // デフォルトはマップ選択画面
  return { mode: 'map-selection', mapId: null };
}

function App() {
  // ルーティング判定（URLパスで切り替え）
  const routeInfo = getAppRouteInfo();

  // 選択されたマップのゲーム定義を取得
  const gameEntry = useMemo(() => {
    if (routeInfo.mapId) {
      return getGameEntry(routeInfo.mapId);
    }
    return null;
  }, [routeInfo.mapId]);

  // Boardgame.io Clientを作成（ゲームモード時のみ）
  const GameClient = useMemo(() => {
    if (!gameEntry) return null;
    return Client({
      game: gameEntry.game,
      board: gameEntry.board,
      numPlayers: gameEntry.numPlayers,
      debug: false  // デバッグパネル非表示
    });
  }, [gameEntry]);

  // エディタモード
  if (routeInfo.mode === 'editor') {
    return (
      <I18nProvider>
        <div style={{
          width: '100vw',
          height: '100vh',
          margin: 0,
          padding: 0,
          overflow: 'hidden'
        }}>
          {/* エディタヘッダー */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '40px',
            backgroundColor: 'hsl(var(--card))',
            borderBottom: '1px solid hsl(var(--border))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            zIndex: 100,
          }}>
            <h1 style={{ fontSize: '14px', fontWeight: 600 }}>Map Editor</h1>
            <a
              href={BASE_PATH}
              style={{
                fontSize: '12px',
                color: 'hsl(var(--primary))',
                textDecoration: 'none',
              }}
            >
              Back to Maps
            </a>
          </div>
          <div style={{ paddingTop: '40px', height: '100%' }}>
            <MapEditor />
          </div>
        </div>
      </I18nProvider>
    );
  }

  // Hall of Honor
  if (routeInfo.mode === 'hall-of-honor') {
    const params = new URLSearchParams(window.location.search);
    const initialMapType = params.get('map') || undefined;
    const highlightRank = params.get('rank') ? parseInt(params.get('rank')!, 10) : undefined;
    return (
      <I18nProvider>
        <HallOfHonor initialMapType={initialMapType} highlightRank={highlightRank} />
      </I18nProvider>
    );
  }

  // Achievements
  if (routeInfo.mode === 'achievements') {
    return (
      <I18nProvider>
        <AchievementsScreen />
      </I18nProvider>
    );
  }

  // Statistics
  if (routeInfo.mode === 'statistics') {
    return (
      <I18nProvider>
        <StatisticsScreen />
      </I18nProvider>
    );
  }

  // Replay Viewer
  if (routeInfo.mode === 'replay') {
    const params = new URLSearchParams(window.location.search);
    const replayMapType = (params.get('map') || '').replace(/[^a-z0-9_]/g, '');
    const replayRank = Math.max(1, Math.min(10, parseInt(params.get('rank') || '1', 10) || 1));
    const replayNoUndo = params.get('noUndo') === 'true';
    return (
      <I18nProvider>
        <div style={{
          width: '100vw',
          height: '100vh',
          margin: 0,
          padding: 0,
          overflow: 'hidden'
        }}>
          <ReplayViewer mapType={replayMapType} rank={replayRank} noUndo={replayNoUndo} />
        </div>
      </I18nProvider>
    );
  }

  // マップ選択画面
  if (routeInfo.mode === 'map-selection') {
    return (
      <I18nProvider>
        <MapSelectionScreen showWIP={true} />
      </I18nProvider>
    );
  }

  // ゲームモード
  if (!GameClient) {
    // フォールバック: マップ選択画面に戻す
    window.location.href = BASE_PATH;
    return null;
  }

  return (
    <I18nProvider>
      <div style={{
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        overflow: 'hidden'
      }}>
        <GameClient />
      </div>
    </I18nProvider>
  );
}

export default App;
